import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { AnimatePresence } from 'framer-motion';
import { HelmetProvider } from 'react-helmet-async';
import Layout from './components/layout/Layout';
import ScrollToTop from './components/common/ScrollToTop';
import ErrorBoundary from './components/common/ErrorBoundary';
import Login from './pages/admin/Login';
import Dashboard from './pages/admin/Dashboard';
import EventsPage from './pages/admin/EventsPage';
import MessagesPage from './pages/admin/MessagesPage';
import CampRegistrationAdmin from './pages/admin/CampRegistrationAdmin';
import CampAdminDashboard from './pages/admin/CampAdminDashboard';
import CampQrCodesPage from './pages/admin/CampQrCodesPage';
import CampCheckInsPage from './pages/admin/CampCheckInsPage';
import theme from './styles/Theme';

// Lazy load pages
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const NextSteps = lazy(() => import('./pages/NextSteps'));
const Ministries = lazy(() => import('./pages/Ministries'));
const Give = lazy(() => import('./pages/Give'));
const Contact = lazy(() => import('./pages/Contact'));
const Events = lazy(() => import('./pages/Events'));
const Sermons = lazy(() => import('./pages/Sermons'));
const CampRegistration = lazy(() => import('./pages/CampRegistration'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  if (!token) {
    return <Navigate to="/admin" replace />;
  }
  return children;
};

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <Router>
          <ErrorBoundary>
            <ScrollToTop />
            <AnimatePresence mode="wait">
              <Routes>
                {/* Admin Routes */}
                <Route path="/admin">
                  <Route index element={<Login />} />
                  <Route 
                    path="dashboard" 
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    } 
                  />
                  <Route
                    path="events"
                    element={
                      <ProtectedRoute>
                        <EventsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="messages"
                    element={
                      <ProtectedRoute>
                        <MessagesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="camp-registration"
                    element={
                      <ProtectedRoute>
                        <CampRegistrationAdmin />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="camp-dashboard"
                    element={
                      <ProtectedRoute>
                        <CampAdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="camp-qr-codes"
                    element={
                      <ProtectedRoute>
                        <CampQrCodesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="camp-checkins"
                    element={
                      <ProtectedRoute>
                        <CampCheckInsPage />
                      </ProtectedRoute>
                    }
                  />
                </Route>

                {/* Public Routes */}
                <Route path="/" element={<Layout />}>
                  <Route index element={
                    <Suspense fallback={<div>Loading...</div>}>
                      <Home />
                    </Suspense>
                  } />
                  <Route path="about" element={
                    <Suspense fallback={<div>Loading...</div>}>
                      <About />
                    </Suspense>
                  } />
                  <Route path="next-steps" element={
                    <Suspense fallback={<div>Loading...</div>}>
                      <NextSteps />
                    </Suspense>
                  } />
                  <Route path="ministries" element={
                    <Suspense fallback={<div>Loading...</div>}>
                      <Ministries />
                    </Suspense>
                  } />
                  <Route path="give" element={
                    <Suspense fallback={<div>Loading...</div>}>
                      <Give />
                    </Suspense>
                  } />
                  <Route path="contact" element={
                    <Suspense fallback={<div>Loading...</div>}>
                      <Contact />
                    </Suspense>
                  } />
                  <Route path="events" element={
                    <Suspense fallback={<div>Loading...</div>}>
                      <Events />
                    </Suspense>
                  } />
                  <Route path="sermons" element={
                    <Suspense fallback={<div>Loading...</div>}>
                      <Sermons />
                    </Suspense>
                  } />
                  <Route path="camp-registration/tssc-2026" element={
                    <Suspense fallback={<div>Loading...</div>}>
                      <CampRegistration />
                    </Suspense>
                  } />
                  <Route path="*" element={
                    <Suspense fallback={<div>Loading...</div>}>
                      <NotFound />
                    </Suspense>
                  } />
                </Route>
              </Routes>
            </AnimatePresence>
          </ErrorBoundary>
        </Router>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App; 
